import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { deliveryTasksKeys } from "@/application/query-keys/delivery-tasks.keys";
import {
  deliveryTasksRepository,
  type CreateDeliveryTaskInput,
  type DeliveryTaskFilters,
  type DeliveryTaskPatch,
} from "@/infrastructure/repositories/delivery-tasks.supabase.repository";

/**
 * Delivery tasks, grouped by sprint (a block is an optional tag on a task, not
 * its scope). RLS returns rows only to an admin, so `enabled` keeps the query
 * from firing (and caching an empty list) before the caller knows whether the
 * user is an admin.
 */
export function useDeliveryTasks(
  filters: DeliveryTaskFilters = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: deliveryTasksKeys.list(filters),
    queryFn: () => deliveryTasksRepository.list(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateDeliveryTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDeliveryTaskInput) => deliveryTasksRepository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryTasksKeys.all });
    },
  });
}

export function useUpdateDeliveryTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: DeliveryTaskPatch }) =>
      deliveryTasksRepository.update(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryTasksKeys.all });
    },
  });
}

export function useDeleteDeliveryTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deliveryTasksRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryTasksKeys.all });
    },
  });
}
